import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

export const ABCD_LETTERS = ['A', 'B', 'C', 'D'];
export const ABCD_MIN_OPTIONS = 2;
export const ABCD_MAX_OPTIONS = 4;

// Grades every question: a question is correct only when the student picked
// its correctOptionId. Unanswered questions count as wrong.
export const gradeAbcd = (questions = [], answers = {}) => {
  const perQuestionResults = {};
  questions.forEach((q) => {
    perQuestionResults[q.id] = !!answers?.[q.id] && answers[q.id] === q.correctOptionId;
  });
  const earnedItems = Object.values(perQuestionResults).filter(Boolean).length;
  const isCorrect = questions.length > 0 && earnedItems === questions.length;
  return { perQuestionResults, isCorrect, earnedItems, totalItems: questions.length };
};

// Text import format (one block per question):
//   1. What is the capital of Ukraine?
//   a) Lviv
//   b) Kyiv *
//   c) Odesa
// The correct option is marked with `*` or `+` at the start or end of the line.
// Options may also start with `-` / `•`. Questions with fewer than 2 options
// are dropped; extra options past 4 are ignored. Without a marker the first
// option is treated as correct.
const OPTION_RE = /^\s*(?:[A-Da-dА-Га-г][).:]|[-•–])\s*(.*)$/;
const CORRECT_MARK_RE = /^\s*[*+]\s*|\s*[*+]\s*$/;

export const parseAbcdText = (raw) => {
  const lines = (raw || '').split(/\r?\n/);
  const blocks = [];
  let current = null;
  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };
  lines.forEach((line) => {
    if (!line.trim()) { flush(); return; }
    const optMatch = line.match(OPTION_RE);
    if (optMatch && current) {
      let text = optMatch[1];
      const isCorrect = CORRECT_MARK_RE.test(text) || /\(\s*correct\s*\)\s*$/i.test(text);
      text = text.replace(/\(\s*correct\s*\)\s*$/i, '').replace(CORRECT_MARK_RE, '').trim();
      if (text) current.options.push({ text, isCorrect });
      return;
    }
    flush();
    current = { text: line.trim().replace(/^\d+\s*[).:]\s*/, ''), options: [] };
  });
  flush();

  return blocks
    .filter((b) => b.options.length >= ABCD_MIN_OPTIONS)
    .map((b) => {
      const options = b.options.slice(0, ABCD_MAX_OPTIONS);
      const correctIdx = Math.max(0, options.findIndex((o) => o.isCorrect));
      return { text: b.text, options: options.map((o) => o.text), correctIdx };
    });
};

const AbcdElement = ({
  element,
  isEditor = false,
  answers: externalAnswers = null,
  showResult = false,
  perQuestionResults = null,
  onAnswersChange,
  onCheck,
  disabled = false,
  hideCheckButton = false,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content } = element;
  const {
    question = '',
    questions = [],
    optionsLayout = 'row',
    fontFamily = 'Inter',
    fontSize = 18,
    textColor = '#1f2937',
    circleColor = '#2A4D25',
    buttonColor = '#2A4D25',
    actionBarColor = '#f9fafb',
  } = content || {};

  const [answers, setAnswers] = useState(externalAnswers || {});
  useEffect(() => {
    if (externalAnswers) setAnswers(externalAnswers);
  }, [externalAnswers]);

  const scrollRef = useRef(null);
  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: questions.length,
    onAutoGrow,
  });

  const handleSelect = (questionId, optionId) => {
    if (isEditor || disabled || showResult) return;
    const next = { ...answers, [questionId]: optionId };
    setAnswers(next);
    onAnswersChange?.(next);
  };

  const anyAnswered = questions.some((q) => !!answers[q.id]);
  const isActionBarTransparent = actionBarColor === 'transparent';

  const renderOption = (q, opt, optIdx) => {
    // In the editor the correct option is circled so the author sees the key.
    const selected = isEditor ? q.correctOptionId === opt.id : answers[q.id] === opt.id;
    const perResult = showResult && perQuestionResults ? perQuestionResults[q.id] : null;
    const isKey = q.correctOptionId === opt.id;

    let ring = 'transparent';
    let ringStyle = 'solid';
    let bg = 'transparent';
    if (selected) {
      ring = circleColor;
      if (showResult) {
        ring = perResult ? '#22c55e' : '#ef4444';
        bg = perResult ? '#dcfce7' : '#fee2e2';
      }
    } else if (showResult && perResult === false && isKey) {
      // Show the right answer next to a wrong pick.
      ring = '#22c55e';
      ringStyle = 'dashed';
    }

    const interactive = !isEditor && !disabled && !showResult;
    return (
      <button
        key={opt.id}
        type="button"
        onClick={() => handleSelect(q.id, opt.id)}
        disabled={!interactive}
        className={`inline-flex items-center gap-2 rounded-full text-left transition ${interactive ? 'cursor-pointer hover:bg-black/5' : 'cursor-default'}`}
        style={{
          border: `${Math.max(2, Math.round(fontSize / 8))}px ${ringStyle} ${ring}`,
          backgroundColor: bg,
          padding: `${Math.round(fontSize * 0.2)}px ${Math.round(fontSize * 0.7)}px`,
          fontFamily,
          fontSize: `${fontSize}px`,
          color: textColor,
          lineHeight: 1.3,
          maxWidth: '100%',
        }}
      >
        <span className="font-semibold flex-none">{ABCD_LETTERS[optIdx]})</span>
        <span style={{ overflowWrap: 'anywhere' }}>{opt.text}</span>
      </button>
    );
  };

  return (
    <div className="w-full h-full rounded-xl overflow-hidden flex flex-col" style={{ backgroundColor: 'transparent' }}>
      <div ref={scrollRef} className="flex-1 min-h-0 min-w-0 p-5 overflow-y-auto overflow-x-hidden">
        {question && (
          <p
            className="font-medium mb-3"
            style={{ fontSize: `${fontSize}px`, fontFamily, color: textColor, lineHeight: 1.3 }}
          >
            {question}
          </p>
        )}

        {questions.length === 0 ? (
          <p className="text-sm text-gray-400">{t('games.abcd.addQuestionsInSettings')}</p>
        ) : (
          <div className="space-y-4">
            {questions.map((q, idx) => (
              <div key={q.id}>
                <p
                  style={{
                    fontSize: `${fontSize}px`,
                    fontFamily,
                    color: textColor,
                    lineHeight: 1.4,
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    marginBottom: `${Math.round(fontSize * 0.35)}px`,
                  }}
                >
                  <span className="font-semibold">{idx + 1}. </span>
                  {q.text || <span className="text-gray-400">{t('games.abcd.questionFallback', { index: idx + 1 })}</span>}
                </p>
                <div
                  className={optionsLayout === 'column' ? 'flex flex-col items-start gap-1.5' : 'flex flex-wrap items-center gap-2'}
                >
                  {(q.options || []).map((opt, optIdx) => renderOption(q, opt, optIdx))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!isEditor && !hideCheckButton && !showResult && (
        <div
          className={`flex items-center justify-end gap-2 ${isActionBarTransparent ? '' : 'border-t border-gray-100'}`}
          style={{ backgroundColor: actionBarColor, padding: '8px 12px' }}
        >
          <button
            onClick={() => onCheck?.(answers)}
            disabled={!anyAnswered}
            className="px-4 py-1.5 text-sm font-medium rounded-lg transition text-white disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
            style={{ backgroundColor: anyAnswered ? buttonColor : undefined }}
          >
            {t('games.common.check')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AbcdElement;
